import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { IdleTimeoutModalComponent } from './idle-timeout-modal/idle-timeout-modal.component';
import { AuthService } from '../service/auth.service';
import { AuthEffects } from '../state/auth.effects';
import { EMPTY, map, Observable, Subject, Subscription, switchMap, take, takeUntil, timer } from 'rxjs';
import { Store } from '@ngrx/store';
import { AuthActions } from '../../auth/state/action-types';
import { AppState } from 'src/app/reducers';
import { CleanDatePipe } from 'src/app/pipes/clean-date.pipe';
import { HelperService } from 'src/app/service/helper.service';

@Component({
    selector: 'app-idle-timeout',
    templateUrl: './idle-timeout.component.html',
    styleUrl: './idle-timeout.component.scss'
})
export class IdleTimeoutComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private authSub?: Subscription;
    private authTimerSub?: Subscription;
    private stopTimer$ = new Subject<void>();

    dialogOpen: boolean = false;
    isLoggedIn: boolean = false;

    timerStarted: boolean = false;

    cleanDatePipe: CleanDatePipe = new CleanDatePipe();

    constructor(
        private dialog: MatDialog,
        private authService: AuthService,
        private authEffects: AuthEffects,
        private store: Store<AppState>,
        private helperService: HelperService
    ) {}

    ngOnInit(): void {
        this.authSub = this.authEffects.isLoggedIn$.subscribe((x: boolean) => {
            if (x) {
                if (!this.timerStarted) {
                    this.startTimeoutWatcher();
                    this.timerStarted = true;
                }
            } else {
                this.stopTimeoutWatcher();
                this.timerStarted = false;
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopTimer$.next();
    }

    startTimeoutWatcher() {
        const WARNING_OFFSET_MS = 60_000; // 1 minute
        const FAST_POLL_MS = 5000; // when close to expiration
        const MIN_WAIT_MS = 1000; // safety floor

        this.authTimerSub?.unsubscribe();

        this.authTimerSub = this.authEffects.isLoggedIn$
            .pipe(
                switchMap((isLoggedIn: boolean) => {
                    if (!isLoggedIn) {
                        return EMPTY;
                    }

                    // recursive scheduling stream
                    return timer(0).pipe(switchMap(() => this.runTimeoutCycle(WARNING_OFFSET_MS, FAST_POLL_MS, MIN_WAIT_MS)));
                }),
                takeUntil(this.destroy$),
                takeUntil(this.stopTimer$)
            )
            .subscribe();
    }

    private runTimeoutCycle(WARNING_OFFSET_MS: number, FAST_POLL_MS: number, MIN_WAIT_MS: number): Observable<any> {
        return this.authService.timeoutCheck().pipe(
            switchMap((res) => {
                if (!(res && res.msg?.SUCCESS && res.expirationTime)) {
                    this.store.dispatch(AuthActions.logout());
                    return EMPTY;
                }

                const currentDateTime = this.cleanDatePipe.transform(res.currentDateTime);
                const cleanExpiration = this.cleanDatePipe.transform(res.expirationTime);

                const expiration = new Date(cleanExpiration - 1_000).getTime();
                const now = new Date(currentDateTime).getTime();

                const timeDiff = expiration - now;

                console.warn(`Adaptive check: timeDiff=${timeDiff}ms`);

                // Expired
                if (timeDiff <= 0) {
                    this.store.dispatch(AuthActions.logout());
                    return EMPTY;
                }

                // Within warning window
                if (timeDiff <= WARNING_OFFSET_MS) {
                    if (!this.dialogOpen) {
                        this.openDialog(timeDiff);
                    }

                    // Poll frequently now
                    return timer(FAST_POLL_MS).pipe(switchMap(() => this.runTimeoutCycle(WARNING_OFFSET_MS, FAST_POLL_MS, MIN_WAIT_MS)));
                }

                // Far from expiration → sleep until close to warning window
                const waitTime = Math.max(timeDiff - WARNING_OFFSET_MS, MIN_WAIT_MS);

                return timer(waitTime).pipe(switchMap(() => this.runTimeoutCycle(WARNING_OFFSET_MS, FAST_POLL_MS, MIN_WAIT_MS)));
            })
        );
    }

    // Call this when user logs out
    stopTimeoutWatcher() {
        this.authTimerSub?.unsubscribe();
        this.stopTimer$.next();
    }

    idleCleanup() {
        this.dialog.closeAll();
    }

    openDialog(remainingMs: number) {
        if (this.dialogOpen) {
            return;
        }

        this.dialogOpen = true;
        let payload: any = {};
        payload.remainingMs = remainingMs;

        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.data = payload;
        dialogConfig.minWidth = '50vw';
        const dialogRef = this.dialog.open(IdleTimeoutModalComponent, dialogConfig);

        dialogRef.afterClosed().subscribe((res: any) => {
            if (res) {
                if (res.forceLogout) {
                    this.stopTimer$.next(); // stop everything immediately

                    this.saveTimeoutSession();
                    this.store.dispatch(AuthActions.logout());
                } else if (res.logout) {
                    this.stopTimer$.next(); // also stop here if you want consistent behavior
                    this.store.dispatch(AuthActions.logout());
                } else if (res.stayLoggedIn) {
                    this.authService.ping();

                    // restart cleanly
                    this.stopTimer$.next();
                    this.startTimeoutWatcher();
                } else {
                    this.stopTimer$.next();
                    this.store.dispatch(AuthActions.logout());
                }
            }

            this.dialogOpen = false;
        });
    }

    saveTimeoutSession() {
        let currSessionTrackingSub = this.helperService.currSessionTracking$
            .pipe(
                take(1),

                map((currSession) => {
                    const updatedSession = {
                        ...currSession,
                        timedOut: true
                    };

                    return updatedSession;
                }),

                switchMap((updatedSession) => this.authService.storeSessionTracking2(updatedSession))
            )
            .subscribe({
                next: (res) => {
                    currSessionTrackingSub.unsubscribe();
                },
                error: (err) => {
                    currSessionTrackingSub.unsubscribe();
                }
            });
    }
}
